// PlayFirst Parent — "My Toys" library.
// Parents add specific toys/interests (name + optional photo) and on-demand
// generate a GLP/Hanen-informed play idea tailored to that exact toy.
//
// Routes used:
//   GET    /parent-app/children/:childId/toys
//   POST   /parent-app/children/:childId/toys
//   DELETE /parent-app/children/:childId/toys/:toyId
//   POST   /parent-app/children/:childId/toys/:toyId/play-idea
//   GET    /parent-app/children/:childId/toys/:toyId/play-ideas
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Sparkles, Loader2, Image as ImageIcon, Trash2, RefreshCw,
  Share2,
} from 'lucide-react';
import { PFP } from './styles';
import { shareParentPlayIdeaPdf } from '@/lib/shareParentPlayIdeaPdf';
import { pickActiveChild, setActiveChildId } from './childSelection';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Resize an image File to <=900px on the longest edge and return a
// data:image/jpeg;base64,... URI. Keeps the payload well under our 700KB
// backend cap.
async function fileToCompressedDataUri(file) {
  const dataUri = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUri;
  });
  const max = 900;
  let { width, height } = img;
  if (width > max || height > max) {
    if (width >= height) {
      height = Math.round(height * (max / width));
      width = max;
    } else {
      width = Math.round(width * (max / height));
      height = max;
    }
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d').drawImage(img, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', 0.82);
}

function PlayIdeaCard({ idea, childFirstName }) {
  const [sharing, setSharing] = useState(false);

  const a = idea?.activity || {};
  const onShare = async () => {
    if (!idea?.id) return;
    setSharing(true);
    try {
      const status = await shareParentPlayIdeaPdf({
        playIdeaId: idea.id,
        activityName: a.activity_name,
        childFirstName,
      });
      if (status === 'shared') toast.success('Shared ✨');
      else if (status === 'downloaded') toast.success('PDF downloaded');
      else if (status === 'cancelled') { /* user dismissed */ }
      else toast.error('Could not share the PDF');
    } finally {
      setSharing(false);
    }
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="mt-3 rounded-2xl border border-[#7C6F9C]/15 bg-white/85 backdrop-blur-md p-4 space-y-3"
      data-testid="toy-play-idea-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-[#7C6F9C] font-medium">Play idea</p>
          <h4 className="font-serif text-lg text-[#3a2f4f] mt-0.5">{a.activity_name}</h4>
          {a.best_routine && (
            <p className="text-xs text-stone-500 mt-1">Best routine: {a.best_routine}</p>
          )}
        </div>
        {a.time_needed && (
          <span className="text-xs whitespace-nowrap text-stone-500">{a.time_needed}</span>
        )}
      </div>

      {(a.what_we_saw || a.why_this_idea) && (
        <div
          className="rounded-xl bg-[#7C6F9C]/8 border border-[#7C6F9C]/15 p-3 space-y-1"
          data-testid="toy-play-idea-from-video"
        >
          {a.what_we_saw && (
            <p className="text-sm text-stone-700">
              <span className="font-medium text-[#5b4f7a]">What we saw:</span> {a.what_we_saw}
            </p>
          )}
          {a.why_this_idea && (
            <p className="text-sm text-stone-700">
              <span className="font-medium text-[#5b4f7a]">Why this idea:</span> {a.why_this_idea}
            </p>
          )}
        </div>
      )}

      {a.what_to_do && (
        <p className="text-sm text-stone-700 leading-relaxed">{a.what_to_do}</p>
      )}

      {Array.isArray(a.phrases_to_model) && a.phrases_to_model.length > 0 && (
        <div className="rounded-xl bg-[#7C6F9C]/5 border border-[#7C6F9C]/15 p-3">
          <p className="text-xs uppercase tracking-wider text-[#7C6F9C] font-medium mb-1">
            Phrases to model
          </p>
          <ul className="text-sm text-stone-700 space-y-0.5">
            {a.phrases_to_model.map((p, i) => (
              <li key={i}>· {p}</li>
            ))}
          </ul>
        </div>
      )}

      {a.what_to_notice && (
        <p className="text-sm text-stone-600">
          <span className="font-medium">What to notice:</span> {a.what_to_notice}
        </p>
      )}

      {a.strategy_to_try && (
        <div
          className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900"
          data-testid="toy-play-idea-strategy"
        >
          <p className="text-xs uppercase tracking-wider text-amber-700 font-medium mb-0.5">
            Gentle strategy · try this today
          </p>
          {a.strategy_to_try}
        </div>
      )}

      {a.glp_note && (
        <div className="rounded-xl bg-emerald-50/60 border border-emerald-200/70 p-3 text-xs text-emerald-900">
          {a.glp_note}
        </div>
      )}

      {a.encouraging_reminder && (
        <p className="text-xs text-stone-500 italic">{a.encouraging_reminder}</p>
      )}

      <div className="pt-1">
        <button
          onClick={onShare}
          disabled={sharing}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#7C6F9C]/30 text-[#7C6F9C] hover:bg-[#7C6F9C]/5 text-sm font-medium transition disabled:opacity-60"
          data-testid="share-toy-play-idea-pdf-btn"
          title="Share as PDF — perfect for nannies, grandparents, or other caregivers"
        >
          {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
          {sharing ? 'Preparing…' : 'Share PDF'}
        </button>
      </div>
    </motion.div>
  );
}

function ToyCard({ toy, latestIdea, onGenerate, onDelete, generating, childFirstName }) {
  // latestIdea is the prop — render directly. (Earlier we had a local
  // showIdea state but it triggered set-state-in-effect; the simpler model
  // is "if we have an idea, show it".)
  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`${PFP.card} p-5`}
      data-testid={`toy-card-${toy.id}`}
    >
      <div className="flex items-start gap-4">
        {toy.image_data ? (
          <img
            src={toy.image_data}
            alt={toy.name}
            className="w-20 h-20 rounded-2xl object-cover border border-stone-200 flex-shrink-0"
            data-testid={`toy-image-${toy.id}`}
          />
        ) : (
          <div className="w-20 h-20 rounded-2xl bg-[#7C6F9C]/10 text-[#7C6F9C] flex items-center justify-center flex-shrink-0">
            <ImageIcon className="w-7 h-7" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-lg text-[#3a2f4f]" data-testid={`toy-name-${toy.id}`}>
            {toy.name}
          </h3>
          {toy.description && (
            <p className="text-sm text-stone-600 mt-0.5 line-clamp-2">{toy.description}</p>
          )}
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              onClick={() => onGenerate(toy)}
              disabled={generating}
              className={PFP.btnPrimary + ' inline-flex items-center gap-2 text-sm py-2'}
              data-testid={`generate-toy-idea-btn-${toy.id}`}
            >
              {generating
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : (latestIdea ? <RefreshCw className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />)}
              {generating
                ? 'Generating…'
                : (latestIdea ? 'Generate a new idea' : 'Generate a play idea')}
            </button>
            <button
              onClick={() => onDelete(toy)}
              className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-rose-500 px-3 py-2 rounded-full"
              data-testid={`delete-toy-btn-${toy.id}`}
              title="Remove toy"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {latestIdea && <PlayIdeaCard idea={latestIdea} childFirstName={childFirstName} />}
    </motion.section>
  );
}

function AddToyForm({ onAdded, onCancel, childId }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    setFile(f);
    try {
      const data = await fileToCompressedDataUri(f);
      setPreview(data);
    } catch {
      toast.error('Could not read that image — try a different one');
      setFile(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        description: description.trim(),
        image_data: preview || null,
      };
      const res = await axios.post(`${API}/parent-app/children/${childId}/toys`, body, auth());
      toast.success('Toy added 🧸');
      onAdded(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save toy');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${PFP.card} p-5`}
      data-testid="add-toy-form"
    >
      <h3 className="font-serif text-lg text-[#3a2f4f] mb-3">Save a toy or interest</h3>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-stone-500 mb-1 block">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Wooden train · Peppa Pig · Dinosaur figures"
            maxLength={80}
            required
            className="w-full px-4 py-2.5 rounded-2xl border border-stone-200 focus:border-[#7C6F9C] focus:outline-none text-sm"
            data-testid="toy-name-input"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-stone-500 mb-1 block">Anything else? (optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="How they like to play with it, what they say, parts they love…"
            maxLength={400}
            rows={2}
            className="w-full px-4 py-2.5 rounded-2xl border border-stone-200 focus:border-[#7C6F9C] focus:outline-none text-sm resize-none"
            data-testid="toy-description-input"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-stone-500 mb-1 block">Photo (optional)</label>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onFile}
            className="hidden"
            data-testid="toy-photo-input"
          />
          {preview ? (
            <div className="flex items-start gap-3">
              <img src={preview} alt="preview" className="w-24 h-24 rounded-2xl object-cover border border-stone-200" />
              <button
                type="button"
                onClick={() => { setPreview(null); setFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                className="text-xs text-stone-500 underline hover:text-stone-700"
                data-testid="remove-toy-photo-btn"
              >Remove photo</button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-dashed border-stone-300 text-sm text-stone-600 hover:border-[#7C6F9C]/50 hover:text-[#7C6F9C]"
              data-testid="upload-toy-photo-btn"
            >
              <ImageIcon className="w-4 h-4" /> Upload a photo
            </button>
          )}
          <p className="text-xs text-stone-400 mt-1">
            A photo helps us tailor the idea to your toy&apos;s parts, sounds and how it moves.
          </p>
        </div>
        <div className="flex items-center gap-2 pt-2">
          <button
            type="submit"
            disabled={saving || !name.trim()}
            className={PFP.btnPrimary + ' inline-flex items-center gap-2 text-sm py-2 disabled:opacity-60'}
            data-testid="save-toy-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {saving ? 'Saving…' : 'Save toy'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className={PFP.btnGhost + ' text-sm py-2'}
            data-testid="cancel-add-toy-btn"
          >
            Cancel
          </button>
        </div>
      </form>
    </motion.section>
  );
}

export default function ParentAppMyToys() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [children, setChildren] = useState([]);
  const [childId, setChildId] = useState(searchParams.get('child') || null);
  const [toys, setToys] = useState([]);
  const [ideasByToy, setIdeasByToy] = useState({}); // toy_id -> latest idea
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [generatingFor, setGeneratingFor] = useState(null);
  // Standalone "Quick idea" — photo only, no toy required. Built
  // specifically for ND children whose interests aren't always discrete toys.
  const [standalonePhotoLoading, setStandalonePhotoLoading] = useState(false);
  const [quickIdea, setQuickIdea] = useState(null);  // single slot — last one wins
  const standalonePhotoRef = useRef(null);

  // Load children once. Prefer the parent's persisted selection so
  // switching child on Home carries into My Toys too.
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/parent-app/children`, auth());
        // `/parent-app/children` returns a raw array (see backend server.py).
        // Guard both shapes so this page keeps working if the endpoint is
        // ever wrapped in `{ children: [...] }` later.
        const list = Array.isArray(res.data)
          ? res.data
          : (res.data?.children || []);
        setChildren(list);
        const picked = pickActiveChild(list);
        if (picked && !childId) setChildId(picked.id);
        if (childId && !list.find((c) => c.id === childId) && picked) {
          setChildId(picked.id);
        }
      } catch (err) {
        if (err?.response?.status === 401) navigate('/parent-app/login');
        toast.error('Could not load your child profile');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load toys + their latest idea whenever child changes
  useEffect(() => {
    if (!childId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/parent-app/children/${childId}/toys`, auth());
        const list = res.data?.toys || [];
        if (cancelled) return;
        setToys(list);
        // Best effort: load latest idea for each toy in parallel.
        const ideaMap = {};
        await Promise.all(list.map(async (t) => {
          try {
            const r = await axios.get(`${API}/parent-app/children/${childId}/toys/${t.id}/play-ideas`, auth());
            if (r.data?.ideas?.length) ideaMap[t.id] = r.data.ideas[0];
          } catch { /* ignore */ }
        }));
        if (!cancelled) setIdeasByToy(ideaMap);
      } catch (err) {
        if (err?.response?.status === 401) navigate('/parent-app/login');
        if (!cancelled) toast.error('Could not load your toys');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [childId, navigate]);

  const currentChild = useMemo(() => children.find((c) => c.id === childId), [children, childId]);

  const handleAdded = (toy) => {
    setToys((prev) => [toy, ...prev]);
    setShowAdd(false);
  };

  const handleDelete = async (toy) => {
    if (!window.confirm(`Remove "${toy.name}" from your toys?`)) return;
    try {
      await axios.delete(`${API}/parent-app/children/${childId}/toys/${toy.id}`, auth());
      setToys((prev) => prev.filter((t) => t.id !== toy.id));
      setIdeasByToy((prev) => { const n = { ...prev }; delete n[toy.id]; return n; });
      toast.success('Toy removed');
    } catch {
      toast.error('Could not remove toy');
    }
  };

  const handleGenerate = async (toy) => {
    setGeneratingFor(toy.id);
    try {
      const res = await axios.post(
        `${API}/parent-app/children/${childId}/toys/${toy.id}/play-idea`,
        {},
        auth(),
      );
      setIdeasByToy((prev) => ({ ...prev, [toy.id]: res.data }));
      toast.success('Here you go ✨');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not generate an idea');
    } finally {
      setGeneratingFor(null);
    }
  };

  const handleGenerateStandalonePhoto = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo too large — please use an image under 5 MB.');
      return;
    }
    setStandalonePhotoLoading(true);
    toast.info('Looking at your photo…');
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const res = await axios.post(
        `${API}/parent-app/children/${childId}/play-idea-from-photo`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      setQuickIdea(res.data);
      toast.success('Here you go ✨');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not generate an idea from that photo');
    } finally {
      setStandalonePhotoLoading(false);
    }
  };

  return (
    <div className={PFP.page} data-testid="my-toys-page">
      <main className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <Link
            to="/parent-app/home"
            className="inline-flex items-center gap-1 text-sm text-[#7C6F9C] hover:underline"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back home
          </Link>
          {children.length > 1 && (
            <Select
              value={childId || ''}
              onValueChange={(id) => { setActiveChildId(id); setChildId(id); }}
            >
              <SelectTrigger className="w-44 h-9 text-sm" data-testid="my-toys-child-switcher">
                <SelectValue placeholder="Pick a child" />
              </SelectTrigger>
              <SelectContent>
                {children.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <header className="space-y-1.5">
          <h1 className={PFP.h2}>Toys &amp; interests</h1>
          <p className={PFP.sub}>
            Anything {currentChild?.name?.split(' ')[0] || 'your child'} is drawn to — toys, a spinning fan,
            running water, a favourite spoon. We&apos;ll meet them where they are.
          </p>
        </header>

        {/* Quick idea — photo entry point (video removed Feb 2026). */}
        <div data-testid="quick-idea-photo-card-wrap">
          <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className={`${PFP.card} p-5 bg-gradient-to-br from-[#7C6F9C]/5 to-white border-[#7C6F9C]/20 flex flex-col`}
            data-testid="standalone-photo-card"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-2xl bg-[#7C6F9C]/15 text-[#7C6F9C] flex items-center justify-center flex-shrink-0">
                <ImageIcon className="w-5 h-5" />
              </div>
              <h3 className="font-serif text-lg text-[#3a2f4f]">Snap a photo</h3>
            </div>
            <p className="text-sm text-stone-600 leading-relaxed flex-1">
              Best for a specific toy or object you&apos;ve got in your hands.
              We&apos;ll generate a play idea and the language to model —
              tailored to that exact thing.
            </p>
            <input
              ref={standalonePhotoRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleGenerateStandalonePhoto(f);
                e.target.value = '';
              }}
              data-testid="standalone-photo-input"
            />
            <button
              type="button"
              onClick={() => standalonePhotoRef.current?.click()}
              disabled={standalonePhotoLoading || !childId}
              className={PFP.btnPrimary + ' mt-4 inline-flex items-center justify-center gap-2 text-sm py-2'}
              data-testid="standalone-photo-btn"
            >
              {standalonePhotoLoading
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <ImageIcon className="w-4 h-4" />}
              {standalonePhotoLoading ? 'Looking at your photo…' : 'Upload a photo'}
            </button>
          </motion.section>
        </div>

        {/* Shared display slot for whichever standalone idea was just generated */}
        {quickIdea && (
          <div data-testid="quick-idea-result">
            <PlayIdeaCard idea={quickIdea} childFirstName={currentChild?.name?.split(' ')[0]} />
          </div>
        )}

        {!showAdd && (
          <button
            onClick={() => setShowAdd(true)}
            className={PFP.btnGhost + ' inline-flex items-center gap-2'}
            data-testid="add-toy-btn"
          >
            <Plus className="w-4 h-4" /> Add a toy or interest to save
          </button>
        )}

        {showAdd && (
          <AddToyForm
            childId={childId}
            onAdded={handleAdded}
            onCancel={() => setShowAdd(false)}
          />
        )}

        {loading ? (
          <div className="flex items-center justify-center py-10 text-stone-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : toys.length === 0 ? (
          <div className={`${PFP.card} p-6 text-center text-stone-500`} data-testid="toys-empty-state">
            <p className="text-sm">No toys yet. Add the first one above 🧸</p>
          </div>
        ) : (
          <div className="space-y-4">
            {toys.map((t) => (
              <ToyCard
                key={t.id}
                toy={t}
                latestIdea={ideasByToy[t.id]}
                generating={generatingFor === t.id}
                onGenerate={handleGenerate}
                onDelete={handleDelete}
                childFirstName={currentChild?.name?.split(' ')[0]}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
